/**
 * The mgmt-api error code and message on a rejection from `httpClient`,
 * wherever normalization left them (#6826; !4880 thread 30530).
 *
 * mgmt-api answers `{"Code", "Message", ...}`. `@aegle/ra-commons` replaces
 * `body` with an `AegleApiError`, which has no code field, so the original JSON
 * survives only under `error.body.raw.body`; the dataProvider re-attaches a
 * lowercase `code` only. Every location is read, because a lookup that misses
 * fails silently. Both are `undefined` when there is no API envelope at all: a
 * bare 404 from a route this API doesn't have, or a network failure.
 *
 * Test fixtures must come from the real `createErrorAwareHttpClient`
 * (`normalizedApiError` in the unit-test helpers), not a hand-built shape.
 */

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;

const firstString = (...candidates: unknown[]): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  }
  return undefined;
};

/** The server's error code, e.g. `COM-000004`. */
export const apiErrorCode = (error: unknown): string | undefined => {
  const body = asRecord(asRecord(error)?.body);
  const rawBody = asRecord(asRecord(body?.raw)?.body);
  return firstString(rawBody?.Code, rawBody?.code, body?.code, body?.Code);
};

/**
 * The server's JSON body on a rejection (e.g. submit-to-aura's `{ failures }` on a
 * 422 or 502), wherever normalization left it.
 */
export const apiErrorBody = (error: unknown): Record<string, unknown> | undefined => {
  const body = asRecord(asRecord(error)?.body);
  return asRecord(asRecord(body?.raw)?.body) ?? body;
};

/** The server's own message. */
export const apiErrorMessage = (error: unknown): string | undefined => {
  const body = asRecord(asRecord(error)?.body);
  const rawBody = asRecord(asRecord(body?.raw)?.body);
  return firstString(rawBody?.Message, rawBody?.message, body?.message, body?.Message);
};
